import * as React from 'react'
import { connect } from 'react-redux'
import * as Types from 'DVWebNodeWpsRuntime'
import * as cn from 'classnames'
import { Link } from '@amzn/dvui/dist/Link'
import { MoreContent } from '@amzn/dvui/dist/MoreContent'
import {
  TextLocalizer,
  withLocalization,
  LocalizerProps
} from '../localizer/localizer'
import { StringId } from '../localizer/stringId'
import { IGetString } from '../../utils/stringUtils'
import {
  isAmazonMaturityRatingBadge,
  isRegulatoryRatingBadge,
  shouldShowAmrDescription
} from '../../utils/ratingBadgeHelper'
import { isLiveEntityType } from '../../utils/entityTypes'
import * as st from './productMetaInfo.module.scss'
import * as stUtility from '../utilityClasses.module.scss'
import { DecodedText } from '../decodedText/decodedText'
import { IDetailPageState } from '../../reducers'
import {
  sanitizedRequestContextSelector,
  MakeUrl
} from '../../selectors/requestContext'
import { ModalTrigger } from '@shared/utils/modalTrigger/ModalTrigger';
import { isCerberusChildSelector, isIosMShopSelector } from '../../selectors/pageContext'
import { isTreatment, safeBoolean } from '../../utils/utils'
import { AtfTreatmentProps } from '../../reducers/features'

const VISIBLE_ITEMS_DEFAULT = 3

export enum Key {
  Directors,
  Starring,
  Genres,
  Subtitles,
  AudioTracks,
  Actors,
  Producers,
  ReleaseYear,
  Studios,
  RegRating,
  PurchaseRights,
  Format,
  Amr,
  Devices
}

interface ICustomizationProps {
  fields: Key[]
  showHeader?: boolean
  expandedItemsCount?: number
  id?: string
}

const mapKeyToExpandedItemsCount: Partial<Record<Key, number>> = {
  [Key.Subtitles]: 2,
  [Key.AudioTracks]: 2,
}

const textLinkTypeContent = (
  text: React.ReactNode,
  href: string,
  key: number
) => [<Link key={`type-content-link-${key}`} text={text} href={href} />]

const textTypeContent = (text: React.ReactNode, key: number) => [
  <React.Fragment key={`type-content-text-${key}`}>{text}</React.Fragment>
]

const amrLinkTypeContent = (
  rating: string,
  desc: string,
  text: React.ReactNode,
  href: string,
  key: number,
  ariaText?: string
) => [
  <span key={`amr-span-${key}`} dir="ltr">{rating}</span>,
  ' ',
  desc,
  ' ',
  <Link key={`amr-link-${key}`} text={text} href={href} ariaLabel={ariaText} />
]

const modalTypeContent = (
  content: React.ReactNode,
  textLink: React.ReactNode,
  key: number
) => (
  <ModalTrigger
    modalContent={content as React.ReactChild}
    toggle={({ openModalClickHandler }) => (
      <Link onClick={openModalClickHandler}>{textLink}</Link>
    )}
    key={`type-content-modal-${key}`}
  />
)

interface IRow {
  header: React.ReactNode
  items: React.ReactNode[][]
  moreString: string
  type: Key
  expandedItemsCount: number,
  stylingClasses?: string[]
}

function regRatingHeader(countryCode: string) {
  switch (countryCode && countryCode.toUpperCase()) {
    case 'US':
      return <TextLocalizer stringID={StringId.DV_TW_title_mpaarating} />
    case 'UK':
      return <TextLocalizer stringID={StringId.DV_TW_title_bbfcrating} />
    case 'DE':
      return <TextLocalizer stringID={StringId.DV_TW_title_fskrating} />
    default:
      return <TextLocalizer stringID={StringId.DV_TW_title_eirinrating} /> // default is 'Rating'
  }
}

function createRowCollection<T>(
  header: React.ReactNode,
  transform: (a: T, index: number) => React.ReactNode[],
  collection?: T[],
  stylingClasses?: string[]
) {
  return {
    header,
    items:
      collection && collection.length > 0 ? collection.map(transform) : null,
    stylingClasses
  }
}

function createRow(header: React.ReactNode, item: React.ReactNode[], stylingClasses?: string[]) {
  return {
    header,
    items: item && item.length > 0 ? [item] : null,
    stylingClasses
  }
}

const transformLinkedContributor = (c: Types.atv.wps.Contributor, idx: number) =>
  textLinkTypeContent(<DecodedText string={c.name} />, c.searchLink, idx)

const transformUnlinkedContributor = (c: Types.atv.wps.Contributor, idx: number) =>
  textTypeContent(<DecodedText string={c.name} />, idx)

const transformLinkedGenre = (g: Types.atv.wps.TitleGenre, idx: number) =>
  textLinkTypeContent(<DecodedText string={g.text} />, g.searchLink, idx)

const transformUnlinkedGenre = (g: Types.atv.wps.TitleGenre, idx: number) =>
  textTypeContent(<DecodedText string={g.text} />, idx)

const transformContent = (content: string, idx: number) =>
  textTypeContent(<DecodedText string={content} />, idx)

const createPurchaseRightsRowData = (makeUrl: MakeUrl) => [
  [
    <TextLocalizer
      stringID={StringId.DV_TW_data_purchaserights_streaming}
      key={0}
    />,
    ' ',
    modalTypeContent(
      <div className={`${st.modalWrapper}`}>
        <TextLocalizer
          stringID={StringId.DV_WEB_DP_popover_purchase_rights}
          dvStringAttrs={{
            compatibleDeviceLink: {
              href: makeUrl('/ontv/devices', {
                refTag: 'atv_dp_hd_dev'
              })
            },
            touLink: {
              href: makeUrl('/dv/tou', {
                refTag: 'atv_dp_tou',
                isShortUrl: true
              })
            },
            learnDownloading: {
              href: makeUrl('/gp/help/customer/display.html?nodeId=201460820', {
                refTag: 'atv_dp_learn_download',
                isShortUrl: true
              })
            }
          }}
        />
      </div>,
      <TextLocalizer stringID={StringId.DV_TW_Detail_text} />,
      1
    )
  ]
]

const createDevicesRowData = (makeUrl: MakeUrl) => [
  [
    textTypeContent(
      <TextLocalizer
        stringID={StringId.DV_TW_data_available_to_watch_devices}
        dvStringAttrs={{
          supportedDevicesLink: {
            href: makeUrl('/watchAnywhere', {
              refTag: 'atv_dp_watch_anywhere',
              isShortUrl: true
            })
          }
        }}
      />,
      0
    )
  ]
]

const createRowData = (
  {
    ratingBadge,
    titleType,
    contributors,
    genres,
    subtitles,
    audioTracks,
    studios,
    releaseYear,
    isPurchaseRestricted,
    entityType,
    requestContext: { makeUrl },
    isCerberusChild,
  }: Types.atv.wps.DetailFragmentModelProps & IStateProps,
  getString: IGetString
) => {
  const amr = isAmazonMaturityRatingBadge(ratingBadge) && ratingBadge
  const regRating = isRegulatoryRatingBadge(ratingBadge) && ratingBadge
  const isMovie = titleType && titleType.toUpperCase() === 'MOVIE'
  const brand = getString(StringId.DV_brand_av)
  const amrLearnMoreAriaLabel = getString(StringId.DV_DP_ARIA_learn_more_amr)

  const transformContributor = safeBoolean(isCerberusChild) ? transformUnlinkedContributor : transformLinkedContributor
  const transformGenre = safeBoolean(isCerberusChild) ? transformUnlinkedGenre : transformLinkedGenre

  return {
    [Key.Directors]: createRowCollection(
      <TextLocalizer stringID={StringId.DV_TW_title_director} />,
      transformContributor,
      contributors && contributors.directors
    ),
    [Key.Producers]: createRowCollection(
      <TextLocalizer stringID={StringId.DV_TW_title_producers} />,
      transformContributor,
      contributors && contributors.producers
    ),
    [Key.Actors]: createRowCollection(
      <TextLocalizer stringID={StringId.DV_TW_title_actors} />,
      transformContributor,
      contributors && contributors.supportingActors
    ),
    [Key.Starring]: createRowCollection(
      <TextLocalizer stringID={StringId.DV_TW_title_starring} />,
      transformContributor,
      contributors && contributors.starringActors
    ),
    [Key.Genres]: createRowCollection(
      <TextLocalizer stringID={StringId.DV_TW_title_genres} />,
      transformGenre,
      genres
    ),
    [Key.Subtitles]: createRowCollection(
      <TextLocalizer stringID={StringId.DV_TW_title_subtitles} />,
      transformContent,
      subtitles,
      [st.breakWords]
    ),
    [Key.AudioTracks]: createRowCollection(
      <TextLocalizer stringID={StringId.DV_TW_title_languages} />,
      transformContent,
      audioTracks,
      [st.breakWords]
    ),
    [Key.Studios]: createRowCollection(
      isMovie ? (
        <TextLocalizer stringID={StringId.DV_TW_title_studio} />
      ) : (
        <TextLocalizer stringID={StringId.DV_TW_title_network} />
      ),
      transformContent,
      studios
    ),
    [Key.Amr]: createRow(
      <TextLocalizer stringID={StringId.DV_TW_title_amr} />,
      (amr &&
        amrLinkTypeContent(
          amr && amr.displayText,
          (amr && shouldShowAmrDescription(amr) && amr.description) || '',
          <TextLocalizer stringID={StringId.DV_DP_learn_more} />,
          '/amr/ref=atv_hp_dp_amr',
          0,
          amrLearnMoreAriaLabel
        )) ||
        []
    ),
    [Key.RegRating]: createRow(
      regRating && regRatingHeader(regRating.countryCode),
      (isMovie &&
        regRating &&
        regRating.description &&
        textTypeContent(regRating.description, 0)) ||
        []
    ),
    [Key.ReleaseYear]: createRow(
      <TextLocalizer stringID={StringId.DV_TW_title_seasonyear} />,
      (!isMovie && releaseYear && textTypeContent(releaseYear, 0)) || []
    ),
    [Key.PurchaseRights]: createRow(
      <TextLocalizer stringID={StringId.DV_TW_title_purchaserights} />,
      (!isPurchaseRestricted && createPurchaseRightsRowData(makeUrl)) || []
    ),
    [Key.Format]: createRow(
      <TextLocalizer stringID={StringId.DV_TW_title_format} />,
      textTypeContent(
        <TextLocalizer
          stringID={StringId.DV_TW_data_format_streaming}
          replace={{ brand }}
        />,
        0
      )
    ),
    [Key.Devices]: createRow(
      <TextLocalizer stringID={StringId.DV_TW_title_devices} />,
      (entityType &&
        !isPurchaseRestricted &&
        !isLiveEntityType(entityType) &&
        createDevicesRowData(makeUrl)) ||
        []
    ),
  }
}

const Row = ({ header, items, moreString, type, expandedItemsCount, stylingClasses }: IRow) => {
  const [head, ...tail] = items

  const separatorAdded = tail.reduce(
    (result: React.ReactNode[], item: React.ReactNode[], idx: number) => [
      ...result,
      <span aria-hidden key={`separator-${idx}`}>
        <TextLocalizer stringID={StringId.DV_comma_separator} />
      </span>,
      ...item
    ],
    []
  )
  let elements = [head, ...separatorAdded]
  const visibleThreshold = 2 * expandedItemsCount // multiplied by 2 to account for separators
  if (elements.length > visibleThreshold) {
    const visible = elements.slice(0, visibleThreshold)
    const hidden = elements.slice(visibleThreshold)
    elements = [
      <MoreContent
        visible={visible}
        hidden={hidden}
        key="more-content"
        moreString={moreString}
        index={type}
      />
    ]
  }

  const classNamesOrUndefined = stylingClasses ? cn(stylingClasses) : undefined
  return (
    <dl key={`dl-${type}`} className={classNamesOrUndefined}>
      <dt>{header}</dt>
      <dd>{elements}</dd>
    </dl>
  )
}

type IStateProps = ReturnType<typeof mapStateToProps>

type ProductMetaInfoWithRequestContextType = Types.atv.wps.DetailFragmentModelProps &
  ICustomizationProps &
  LocalizerProps &
  IStateProps &
  AtfTreatmentProps

export class ProductMetaInfoValid extends React.PureComponent<
  ProductMetaInfoWithRequestContextType
  > {
  constructor(props: ProductMetaInfoWithRequestContextType) {
    super(props)
  }

  public render() {
    const {
      fields,
      showHeader = false,
      expandedItemsCount = VISIBLE_ITEMS_DEFAULT,
      id,
      getString,
      atfTreatment
    } = this.props

    const moreString = getString(StringId.DV_DP_more_items)

    const rowData = createRowData(this.props, getString)
    const items = fields.map((f, idx) => {
      const count = mapKeyToExpandedItemsCount[f] || expandedItemsCount
      const row = rowData[f]
      return (
        row &&
        row.items &&
        row.items.length > 0 && (
          <Row
            key={`row-${id}-${idx}`}
            header={row.header}
            items={row.items}
            moreString={moreString}
            type={f}
            expandedItemsCount={count}
            stylingClasses={row.stylingClasses}
          />
        )
      )
    })

    const header =
      showHeader && items && items.filter(Boolean).length ? (
        <TextLocalizer stringID={StringId.DV_DP_more_details} />
      ) : null

    return (
      <div className={cn(stUtility.fullWidth, st.productMetaInfo,
        {[st.productMetaInfoRedesign]: isTreatment(atfTreatment, 'T1', 'T2', 'T3')})} id={id} data-automation-id={id}>
        {header && <h1> {header} </h1>}
        <div className={cn(st.info, {[st.infoRedesign]: isTreatment(atfTreatment, 'T1', 'T2')})}>
          {items}
        </div>
      </div>
    )
  }
}

const mapStateToProps = (state: IDetailPageState) => ({
  requestContext: sanitizedRequestContextSelector(state),
  isPurchaseRestricted: isIosMShopSelector(state),
  isCerberusChild: isCerberusChildSelector(state)
})

export const ProductMetaInfo = withLocalization(
  connect(mapStateToProps)(ProductMetaInfoValid)
)
